import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, Output, ViewChild, EventEmitter, computed } from '@angular/core';

import { userAgentUtil } from 'vbrick-player-src/UserAgentUtil';
import { VBrickPlayerComponent } from 'vbrick-player-src/VbrickPlayer.Component';

import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';
import { VgStates } from 'vbrick-player-src/videogular/VgStates';

import { IPlayerChapter } from './chapters/IPlayerChapter';
import { IScrubAdapter } from './scrubAdapter/IScrubAdapter';
import { ITimeMarker } from './VbTimeMarkers.Component';

import styles from './VgScrubBar.module.less';

/**
 * Component to control the time and display other information layers about the progress of the media.
 * It acts as a container and you can add more layers to display current time, cuepoints, buffer or whatever you need.
 *
 * <pre>
 * <videogular vg-theme="config.theme.url">
 *    <vg-media vg-src="sources"></vg-media>
 *
 *    <vg-controls vg-autohide='config.autohide' vg-autohide-time='config.autohideTime'>
 *        <vg-scrub-bar></vg-scrub-bar>
 *    </vg-controls>
 * </videogular>
 * </pre>
 *
 */
@Component({
	selector: 'vg-scrub-bar',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div
			#slider
			role="slider"
			[attr.aria-valuemax]="ariaTime(adapter.getDuration())"
			[attr.aria-valuenow]="ariaTime(adjustedCurrentTimeMs())"
			aria-valuemin="0"
			[attr.aria-label]="vbrickPlayer.translations.scrubBar"
			[ngClass]="[styles.slider, vbrickPlayer.chapterHelper?.chapters().length ? styles.scrubBarWithChapters : styles.normalScrubBar]"
			tabindex="0"
			vbControlBarFocus
			(keydown)="onScrubBarKeyDown($event)"
		>
			<vg-scrub-bar-scrubber
				[ngStyle]="{ 'left': scrubBarPercentage() }"
				[ngClass]="!vbrickPlayer.chapterHelper?.chapters().length && !timeMarkers?.length ? styles.scrubberWithoutChaptersOrTimeMarkers : ''"
			></vg-scrub-bar-scrubber>

			<vb-scrub-bar-with-chapters
				*ngIf="vbrickPlayer.chapterHelper?.chapters().length; else normalProgressBar"
				[chapters]="vbrickPlayer.chapterHelper.chapters()"
				[adapter]="adapter">
			</vb-scrub-bar-with-chapters>

			<ng-template #normalProgressBar>
				<vb-scrub-bar-progress [progressPercentage]="scrubBarPercentage()">></vb-scrub-bar-progress>
			</ng-template>

			<vb-time-markers
				*ngIf="timeMarkers?.length"
				[ngClass]="vbrickPlayer.chapterHelper?.chapters().length ? styles.timeMarkersWithChapters : ''"
				[markers]="timeMarkers"
				[totalTime]="adapter.getDuration() / 1000">
			</vb-time-markers>
		</div>
	`,
	host: {
		'[class]': 'styles.root + " " + (isSmallSizePlayer ? styles.smallSizePlayer : "")'
	}
})
export class VgScrubBarComponent {
	@Input() public isStartAtSeeking: boolean;
	@Input() public isSmallSizePlayer: boolean;
	@Input() public startAt: number;
	@Input() public timeMarkers: ITimeMarker[];
	@Input() public isThumbnailEnabled: boolean;
	@Input() public adapter: IScrubAdapter;
	@Output() public scrubBarPreviewEvent: EventEmitter<{ timeSecs?: number, isVisible: boolean, position?: string, chapter?: IPlayerChapter }> = new EventEmitter();
	@ViewChild('slider', { static: true }) private sliderRef: ElementRef;

	public readonly styles = styles;

	private isSeeking: boolean = false;
	private isPlayingWhenSeeking: boolean = false;

	constructor(
		private api: VideogularComponent,
		public vbrickPlayer: VBrickPlayerComponent
	) { }

	public readonly adjustedCurrentTimeMs = computed(() => {
		const duration = this.adapter.getDuration();
		const currentTime = this.adapter.getCurrentTime();
		if (this.vbrickPlayer.isLive || !duration) {
			return 0;
		}

		const potentialAdjustedCurrentTimeMs: number = this.isStartAtSeeking ?
			this.startAt * 1000 : // sec => ms
			currentTime;

		return Math.min(potentialAdjustedCurrentTimeMs, duration) ||
			0;
	});

	public readonly scrubBarPercentage = computed(() => {
		const duration = this.adapter.getDuration();
		const currentTime = this.adjustedCurrentTimeMs();
		const percentageValue: number = duration ?
			currentTime / duration * 100:
			0;

		return percentageValue + '%';
	});

	private get isPlaying(): boolean {
		return this.api.currentState === VgStates.PLAY;
	}

	public ariaTime(time: number): number {
		return Math.round(time / 1000);
	}

	private enableAnalytics(): void {
		this.api.analyticsEnabled = true;
	}

	private endSeeking(): void {
		if (this.isPlayingWhenSeeking) {
			this.isPlayingWhenSeeking = false;
			this.api.play();
		}
		this.isSeeking = false;
	}

	private getOffset(event: MouseEvent | Touch): number {
		let el = event.target as HTMLElement;
		let x = 0;

		while (el && !isNaN(el.offsetLeft)) {
			x += el.offsetLeft - el.scrollLeft;
			el = el.offsetParent as HTMLElement;
		}

		return event.clientX - x;
	}

	private getSeekTimeSecs(x: number): number {
		const xFactor = x >= 0 ? (x / this.sliderRef.nativeElement.clientWidth) : 0;
		return this.adapter.onScrubClick(xFactor) / 1000;
	}

	@HostListener('document:mouseup')
	private onDocumentMouseUp(): void {
		if (this.isSeeking) {
			this.onScrubBarMouseUp();
		}
	}

	@HostListener('keydown', ['$event'])
	public onScrubBarKeyDown(event: KeyboardEvent): void {

		if (event.key === 'ArrowLeft') {
			this.api.seek10Secs(false);
			event.preventDefault();
		} else if (event.key === 'ArrowRight') {
			this.api.seek10Secs(true);
			event.preventDefault();
		}
	}

	@HostListener('mousedown', ['$event'])
	private onScrubBarMouseDown(event: MouseEvent): void {
		const parentElement = (event.currentTarget as HTMLElement);
		const offsetX = event.clientX - parentElement.getBoundingClientRect().left;

		if (event.button !== 0 || userAgentUtil.isMobile) {
			return;
		}

		this.silenceAnalytics();

		this.isSeeking = true;

		if (this.isPlaying) {
			this.isPlayingWhenSeeking = true;
		}

		this.api.pause();
		this.api.seekTime(this.getSeekTimeSecs(offsetX));
	}

	@HostListener('mouseleave')
	private onScrubBarMouseLeave(): void {
		if (userAgentUtil.isMobile) {
			return;
		}

		if (this.isThumbnailEnabled) {
			this.emitHidePreview();
		}

		this.isSeeking = false;

		this.enableAnalytics();
	}

	@HostListener('mousemove', ['$event'])
	private onScrubBarMouseMove(event: MouseEvent): void {
		const parentElement = (event.currentTarget as HTMLElement);
		const offsetX = event.clientX - parentElement.getBoundingClientRect().left;

		if (userAgentUtil.isMobile) {
			return;
		}

		if (this.isThumbnailEnabled) {
			this.emitPreviewPosition(offsetX);
		}

		if (this.isSeeking) {
			this.api.seekTime(this.getSeekTimeSecs(offsetX));
		}
	}

	@HostListener('mouseup')
	private onScrubBarMouseUp(): void {
		this.endSeeking();

		this.enableAnalytics();
	}

	@HostListener('touchend')
	private onScrubBarTouchEnd(): void {
		if (!userAgentUtil.isMobile) {
			return;
		}

		if (this.isThumbnailEnabled) {
			this.emitHidePreview();
		}

		this.endSeeking();

		this.enableAnalytics();
	}

	@HostListener('touchleave')
	private onScrubBarTouchLeave(): void {
		if (!userAgentUtil.isMobile) {
			return;
		}

		this.isSeeking = false;

		this.enableAnalytics();
	}

	@HostListener('touchmove', ['$event'])
	private onScrubBarTouchMove($event: TouchEvent): void {
		if (!userAgentUtil.isMobile) {
			return;
		}

		const touchX: number = this.getOffset($event.touches[0]);

		if (this.isThumbnailEnabled) {
			this.emitPreviewPosition(touchX);
		}

		if (this.isSeeking) {
			this.api.seekTime(this.getSeekTimeSecs(touchX));
		}
	}

	@HostListener('touchstart', ['$event'])
	private onScrubBarTouchStart($event: TouchEvent): void {
		if (!userAgentUtil.isMobile) {
			return;
		}

		if (this.isThumbnailEnabled) {
			this.emitHidePreview();
		}

		this.silenceAnalytics();

		const touchX = this.getOffset($event.touches[0]);

		this.isSeeking = true;

		if (this.isPlaying) {
			this.isPlayingWhenSeeking = true;
		}

		this.api.pause();
		this.api.seekTime(this.getSeekTimeSecs(touchX));
	}

	private silenceAnalytics(): void {
		this.api.analyticsEnabled = false;
	}

	private getPreviewPosition(pointerX: number, tileWidth: number): string {
		const sliderRect: DOMRect = this.sliderRef.nativeElement.getBoundingClientRect();
		const minPosition = this.isSmallSizePlayer ? 7 : 21;
		const maxPosition = sliderRect.right - sliderRect.left - tileWidth + minPosition;

		let calculatedPosition = pointerX + minPosition - tileWidth / 2;

		if (calculatedPosition < minPosition) {
			calculatedPosition = minPosition;
		} else if (calculatedPosition > maxPosition) {
			calculatedPosition = maxPosition;
		}

		return `${calculatedPosition}px`;
	}

	private emitPreviewPosition(position: number): void {
		const activePreviewChapter = this.vbrickPlayer.chapterHelper?.getChapterByTimeMs(this.getSeekTimeSecs(position) * 1000);

		this.scrubBarPreviewEvent.emit({
			timeSecs: this.getSeekTimeSecs(position),
			isVisible: true,
			position: this.getPreviewPosition(
				position,
				activePreviewChapter?.url ? this.vbrickPlayer.chapterHelper.chapterPreviewWidth : this.vbrickPlayer.thumbnailCfg.tileResolution.width
			),
			chapter: (activePreviewChapter?.url && !activePreviewChapter?.isFakeInitialChapter) ? activePreviewChapter : undefined
		});
	}

	private emitHidePreview(): void {
		this.scrubBarPreviewEvent.emit({
			isVisible: false
		});
	}

}
